import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import AlertBanner from 'tio-ui/components/alert-banner';
import Button from 'tio-ui/components/button';
import Input from 'tio-ui/components/input';
import Select from 'tio-ui/components/select';
import Text from 'tio-ui/components/text';
import { PhCheck } from 'ember-phosphor-icons';
import { STATE_OPTIONS as stateOptions } from 'tio-common/utils/us-states';
import { reloadRecord, saveRecord } from 'tio-data/requests/save-record';
import {
  verifyBorrowerAddressRequest,
  type VerifyBorrowerAddressResponse,
} from 'tio-data/requests/pslf-form';
import { MAILING_ADDRESS_TYPE } from 'tio-employee/utils/pslf/contact-details';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { PostalAddressExtension as PostalAddressModel } from 'tio-data/data/resources/postal-address.ext';
import type { PslfAnnualAutoSubmitAddress } from 'tio-data/data/resources/pslf-profile.type';
import type { IntlService } from 'ember-intl';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';

/** The verify endpoint takes the ISO code; the stored row keeps the three-letter form the API defaults to. */
const VERIFY_COUNTRY = 'US';
const STORED_COUNTRY = 'USA';

const POSTAL_CODE_LENGTH = 5;

type VerificationStatus = VerifyBorrowerAddressResponse['status'] | 'verifying';
type SuggestedAddress = VerifyBorrowerAddressResponse['suggestedAddress'];

export interface AddressFormSignature {
  Args: {
    /** The employee's existing mailing row; absent the first time they add one. */
    address?: PostalAddressModel;
    /** Starting values when there is no row yet, e.g. the address last year's form filed. */
    prefill?: PslfAnnualAutoSubmitAddress | null;
    /** Owner of a newly created mailing row, and the record refetched so the saved row is readable. */
    person: PersonModel;
    onSaved: () => void;
    onCancel: () => void;
    /** Surfaces the save failure; the form stays open so the entry is not lost. */
    onError: (error: unknown) => void;
  };
}

/** Inline mailing-address editor; verification runs on Save and warns rather than blocks. */
export default class AnnualAddressForm extends Component<AddressFormSignature> {
  get #initial(): PslfAnnualAutoSubmitAddress {
    return this.args.address ?? this.args.prefill ?? {};
  }

  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;
  @service declare store: Store;

  @tracked addressLine1 = this.#initial.addressLine1 ?? '';
  @tracked addressLine2 = this.#initial.addressLine2 ?? '';
  @tracked city = this.#initial.city ?? '';
  @tracked state = this.#initial.state ?? '';
  @tracked postalCode = this.#initial.postalCode ?? '';

  @tracked hasSubmitted = false;
  @tracked verificationStatus?: VerificationStatus;
  @tracked suggestedAddress?: SuggestedAddress;
  @tracked hasSeenVerificationResult = false;

  states = stateOptions.map((option) => ({ key: option.abbr, label: option.name }));

  get sanitizedPostalCode() {
    return this.postalCode.replace(/\D/g, '').slice(0, POSTAL_CODE_LENGTH);
  }

  get isComplete() {
    return (
      !!this.addressLine1.trim() &&
      !!this.city.trim() &&
      !!this.state.trim() &&
      this.sanitizedPostalCode.length === POSTAL_CODE_LENGTH
    );
  }

  #requiredError(isPresent: boolean, key: string) {
    if (!this.hasSubmitted || isPresent) return undefined;
    return [this.intl.t(`pslf.reimagined.annual_auto_submit.details.address.${key}`)];
  }

  get addressLine1Errors() {
    return this.#requiredError(!!this.addressLine1.trim(), 'line1_required');
  }

  get cityErrors() {
    return this.#requiredError(!!this.city.trim(), 'city_required');
  }

  get stateErrors() {
    return this.#requiredError(!!this.state.trim(), 'state_required');
  }

  get postalCodeErrors() {
    return this.#requiredError(
      this.sanitizedPostalCode.length === POSTAL_CODE_LENGTH,
      'postal_code_required'
    );
  }

  get isVerifying() {
    return this.verificationStatus === 'verifying' || this.verify.isRunning;
  }

  get hasSuggestion() {
    return !!this.suggestedAddress?.addressLine1;
  }

  get isVerified() {
    return this.verificationStatus === 'valid';
  }

  get showsUnverifiedWarning() {
    return (
      this.hasSeenVerificationResult &&
      !this.isVerifying &&
      !this.hasSuggestion &&
      !!this.verificationStatus &&
      !this.isVerified
    );
  }

  get formattedSuggestion() {
    const suggestion = this.suggestedAddress;
    if (!suggestion) return '';
    const region = [
      suggestion.city,
      [suggestion.state, suggestion.postalCode].filter(Boolean).join(' '),
    ]
      .filter(Boolean)
      .join(', ');
    return [suggestion.addressLine1, suggestion.addressLine2, region].filter(Boolean).join(', ');
  }

  #resetVerification() {
    this.verificationStatus = undefined;
    this.suggestedAddress = undefined;
    this.hasSeenVerificationResult = false;
  }

  setAddressLine1 = (value: string) => {
    this.addressLine1 = value;
    this.#resetVerification();
  };

  setAddressLine2 = (value: string) => {
    this.addressLine2 = value;
    this.#resetVerification();
  };

  setCity = (value: string) => {
    this.city = value;
    this.#resetVerification();
  };

  setState = (value: string) => {
    this.state = value ?? '';
    this.#resetVerification();
  };

  setPostalCode = (value: string) => {
    this.postalCode = value;
    this.#resetVerification();
  };

  useSuggestedAddress = () => {
    const suggestion = this.suggestedAddress;
    if (!suggestion) return;

    this.addressLine1 = suggestion.addressLine1 ?? this.addressLine1;
    this.addressLine2 = suggestion.addressLine2 ?? '';
    this.city = suggestion.city ?? this.city;
    this.state = suggestion.state ?? this.state;
    this.postalCode = suggestion.postalCode ?? this.postalCode;
    this.suggestedAddress = undefined;
    this.verificationStatus = 'valid';

    this.commit.perform(true);
  };

  keepEnteredAddress = () => {
    this.suggestedAddress = undefined;
    this.commit.perform(this.isVerified);
  };

  verify = dropTask(async () => {
    this.verificationStatus = 'verifying';
    this.suggestedAddress = undefined;

    try {
      const { content } = (await this.store.request(
        verifyBorrowerAddressRequest({
          address: {
            addressLine1: this.addressLine1,
            addressLine2: this.addressLine2 || undefined,
            city: this.city,
            state: this.state,
            postalCode: this.sanitizedPostalCode,
            country: VERIFY_COUNTRY,
          },
        })
      )) as { content: VerifyBorrowerAddressResponse };

      this.verificationStatus = content.status;
      this.suggestedAddress = content.suggestedAddress;
    } catch (error) {
      console.error('Error verifying PSLF mailing address', error);
      this.verificationStatus = 'unverified';
      this.suggestedAddress = undefined;
    }

    this.hasSeenVerificationResult = true;
  });

  save = dropTask(async () => {
    this.hasSubmitted = true;
    if (!this.isComplete) return;

    if (!this.hasSeenVerificationResult) {
      await this.verify.perform();
      // A result the employee has not seen yet is a prompt, not a verdict — let them
      // take the normalised address or re-submit to keep their own.
      if (this.hasSuggestion || !this.isVerified) return;
    }

    await this.commit.perform(this.isVerified);
  });

  commit = dropTask(async (isVerified: boolean) => {
    const existing = this.args.address;

    if (existing) {
      this.#applyTo(existing, isVerified);
      try {
        await saveRecord(this.store, existing);
        this.args.onSaved();
      } catch (error) {
        existing.rollbackAttributes();
        this.args.onError(error);
      }
      return;
    }

    const created = this.store.createRecord<PostalAddressModel>('postal-address', {
      postalAddressType: MAILING_ADDRESS_TYPE,
      postalAddressable: this.args.person,
      user: this.sessionContext.user,
      country: STORED_COUNTRY,
      isActive: true,
    });
    this.#applyTo(created, isVerified);

    try {
      await saveRecord(this.store, created);
    } catch (error) {
      created.unloadRecord();
      this.args.onError(error);
      return;
    }

    // The created row already names the person as its owner, so `person.ownedPostalAddresses`
    // carries it whatever happens next; the refetch only reconciles the through-User
    // `postalAddresses` set the rest of the app reads. `onSaved` stays last because closing the
    // row destroys this component, which would cancel the task mid-refetch.
    try {
      await reloadRecord(this.store, this.args.person, { include: 'postal-addresses' });
    } catch (error) {
      console.error(error);
    }

    this.args.onSaved();
  });

  #applyTo(address: PostalAddressModel, isVerified: boolean) {
    address.addressLine1 = this.addressLine1.trim();
    address.addressLine2 = this.addressLine2.trim();
    address.city = this.city.trim();
    address.state = this.state.trim();
    address.postalCode = this.sanitizedPostalCode;
    address.isVerified = isVerified;
  }

  <template>
    <div class="tio-col-tight pii" data-test-pslf-details-form="address">
      <Input
        @label={{t "pslf.reimagined.annual_auto_submit.details.address.line1_label"}}
        @value={{this.addressLine1}}
        @onChange={{this.setAddressLine1}}
        @errors={{this.addressLine1Errors}}
        @autocomplete="address-line1"
        @testHook="pslf-details__address-line1"
        data-test-pslf-details-input="addressLine1"
      />
      <Input
        @label={{t "pslf.reimagined.annual_auto_submit.details.address.line2_label"}}
        @value={{this.addressLine2}}
        @onChange={{this.setAddressLine2}}
        @autocomplete="address-line2"
        @testHook="pslf-details__address-line2"
        data-test-pslf-details-input="addressLine2"
      />
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Input
          @label={{t "pslf.reimagined.annual_auto_submit.details.address.city_label"}}
          @value={{this.city}}
          @onChange={{this.setCity}}
          @errors={{this.cityErrors}}
          @autocomplete="address-level2"
          @testHook="pslf-details__address-city"
          data-test-pslf-details-input="city"
        />
        <Select
          @label={{t "pslf.reimagined.annual_auto_submit.details.address.state_label"}}
          @items={{this.states}}
          @selected={{this.state}}
          @onChange={{this.setState}}
          @errors={{this.stateErrors}}
          @searchable={{true}}
          @clearable={{false}}
          @testHook="pslf-details__address-state"
          data-test-pslf-details-input="state"
        />
        <Input
          @label={{t "pslf.reimagined.annual_auto_submit.details.address.postal_code_label"}}
          @value={{this.postalCode}}
          @onChange={{this.setPostalCode}}
          @errors={{this.postalCodeErrors}}
          @inputmode="numeric"
          @maxlength={{10}}
          @autocomplete="postal-code"
          @testHook="pslf-details__address-postal-code"
          data-test-pslf-details-input="postalCode"
        />
      </div>

      {{#if this.isVerifying}}
        <Text @variant="muted" @size="sm" data-test-pslf-details-address-verifying>
          {{t "pslf.reimagined.annual_auto_submit.details.address.verifying"}}
        </Text>
      {{else if this.hasSuggestion}}
        <div
          class="app-status-panel app-status-panel--brand p-4 tio-col-tight"
          data-test-pslf-details-address-suggestion
        >
          <Text @size="sm" @weight="semibold">
            {{t "pslf.reimagined.annual_auto_submit.details.address.did_you_mean"}}
          </Text>
          <Text @size="sm" data-test-pslf-details-address-suggested-text>
            {{this.formattedSuggestion}}
          </Text>
          <div class="tio-row-tight flex-wrap">
            <Button
              @intent="primary"
              @size="sm"
              @isRunning={{this.commit.isRunning}}
              @testHook="pslf-details__address-use-suggested"
              data-test-pslf-details-address-use-suggested
              {{on "click" this.useSuggestedAddress}}
            >
              {{t "pslf.reimagined.annual_auto_submit.details.address.use_suggested"}}
            </Button>
            <Button
              @appearance="outlined"
              @intent="default"
              @size="sm"
              @isRunning={{this.commit.isRunning}}
              @testHook="pslf-details__address-keep-mine"
              data-test-pslf-details-address-keep-mine
              {{on "click" this.keepEnteredAddress}}
            >
              {{t "pslf.reimagined.annual_auto_submit.details.address.keep_mine"}}
            </Button>
          </div>
        </div>
      {{else if this.showsUnverifiedWarning}}
        <AlertBanner @intent="warning" data-test-pslf-details-address-warning>
          {{t "pslf.reimagined.annual_auto_submit.details.address.unverified_warning"}}
        </AlertBanner>
      {{else if this.isVerified}}
        <div class="tio-row-snug" data-test-pslf-details-address-verified>
          <PhCheck @size={{16}} class="tio-fg-success" />
          <Text @variant="muted" @size="sm">
            {{t "pslf.reimagined.annual_auto_submit.details.address.verified"}}
          </Text>
        </div>
      {{/if}}

      <div class="tio-row-tight flex-wrap">
        {{#unless this.hasSuggestion}}
          <Button
            @intent="primary"
            @size="sm"
            @isRunning={{this.save.isRunning}}
            @testHook="pslf-details__address-save"
            data-test-pslf-details-save="address"
            {{on "click" this.save.perform}}
          >
            {{#if this.showsUnverifiedWarning}}
              {{t "pslf.reimagined.annual_auto_submit.details.address.save_anyway"}}
            {{else}}
              {{t "pslf.reimagined.annual_auto_submit.details.save"}}
            {{/if}}
          </Button>
        {{/unless}}
        <Button
          @appearance="link"
          @intent="default"
          @size="sm"
          @testHook="pslf-details__address-cancel"
          data-test-pslf-details-cancel="address"
          {{on "click" @onCancel}}
        >
          {{t "pslf.reimagined.annual_auto_submit.details.cancel"}}
        </Button>
      </div>
    </div>
  </template>
}
